import { TrackerDataType } from '../../shared/constants/constants';
import { ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';

type LinkedServiceContextSource = {
  _id?: string;
  id?: string;
  service?: string;
  name?: string;
  type?: { name?: string }[] | string[];
  types?: { name?: string }[] | string[];
  campaign?: { name?: string }[] | string[];
  campaigns?: { name?: string }[] | string[];
  units?: string[] | { name?: string }[];
  dept?: string[] | { name?: string }[];
  depts?: string[] | { name?: string }[];
  unitLead?: ({ _id?: string; id?: string; name?: string } | string)[];
  expert?: ({ _id?: string; id?: string; name?: string } | string)[];
  searchMetadata?: {
    expertNames?: string[];
  };
};

type DropdownTable = Record<string, any>;

export type ParentServiceContext = {
  id: string;
  topic: string;
  types: string[];
  campaigns: string[];
  units: string[];
  departments: string[];
  leadExperts: string[];
};

const uniqueStrings = (values: (string | null | undefined)[]) =>
  [...new Set(values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0))];

const normalizeNameValues = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  return uniqueStrings(
    values.map((entry) => {
      if (typeof entry === 'string') {
        return entry;
      }
      if (entry && typeof entry === 'object') {
        const value = entry as { name?: string; label?: string; value?: string };
        return value.name ?? value.label ?? value.value ?? null;
      }
      return null;
    })
  );
};

export function shouldSkipLinkedServiceTypeAutofill(
  eventHandlerType: string | undefined,
  linkedType: string | undefined
) {
  return eventHandlerType === TrackerDataType.CONTENT_LOG && linkedType === TrackerDataType.SERVICE;
}

export function buildParentServiceContext(
  service: LinkedServiceContextSource | null | undefined,
  dropdownTable: DropdownTable | null | undefined
): ParentServiceContext | null {
  if (!service) {
    return null;
  }

  const expertOptions = Array.isArray(dropdownTable?.expert) ? dropdownTable.expert : [];
  const unitLeadEntries = service.unitLead ?? service.expert ?? [];
  const leadExperts = uniqueStrings(
    unitLeadEntries.map((unitLead) => {
      if (!unitLead) return null;
      if (typeof unitLead === 'string') {
        return expertOptions.find((expert) => expert?._id === unitLead)?.name ?? null;
      }
      if (unitLead.name) {
        return unitLead.name;
      }
      const expertId = unitLead._id ?? unitLead.id;
      return expertOptions.find((expert) => expert?._id === expertId)?.name ?? null;
    })
  );

  return {
    id: service._id ?? service.id ?? '',
    topic: service.service ?? service.name ?? '',
    types: normalizeNameValues(service.type ?? service.types ?? []),
    campaigns: normalizeNameValues(service.campaign ?? service.campaigns ?? []),
    units: normalizeNameValues(service.units ?? []),
    departments: normalizeNameValues(service.dept ?? service.depts ?? []),
    leadExperts: leadExperts.length > 0 ? leadExperts : normalizeNameValues(service.searchMetadata?.expertNames ?? []),
  };
}

export function buildParentServiceContextFromFlatService(
  flatService: { id?: string; cells?: Record<string, unknown> } | null | undefined,
  dropdownTable: DropdownTable | null | undefined
): ParentServiceContext | null {
  if (!flatService?.cells) {
    return null;
  }

  const cells = flatService.cells;
  const expertOptions = Array.isArray(dropdownTable?.expert) ? dropdownTable.expert : [];

  const leadExperts = uniqueStrings(
    (Array.isArray(cells[ServiceKeys.unitLead]) ? (cells[ServiceKeys.unitLead] as unknown[]) : []).map((expert) => {
      if (!expert) return null;
      if (typeof expert === 'string') {
        return expertOptions.find((option) => option?._id === expert)?.name ?? expert;
      }
      if (typeof expert === 'object') {
        const candidate = expert as { name?: string; _id?: string; id?: string };
        if (candidate.name) {
          return candidate.name;
        }
        const expertId = candidate._id ?? candidate.id;
        return expertOptions.find((option) => option?._id === expertId)?.name ?? null;
      }
      return null;
    })
  );

  return {
    id: (flatService.id as string) ?? '',
    topic: (cells[ServiceKeys.service] as string) ?? '',
    types: normalizeNameValues(cells[ServiceKeys.type]),
    campaigns: normalizeNameValues(cells[ServiceKeys.campaign]),
    units: normalizeNameValues(cells[ServiceKeys.units]),
    departments: normalizeNameValues(cells[ServiceKeys.dept]),
    leadExperts,
  };
}

export function mergeParentServiceContexts(
  primary: ParentServiceContext | null,
  fallback: ParentServiceContext | null
): ParentServiceContext | null {
  if (!primary && !fallback) {
    return null;
  }

  const safePrimary = primary ?? {
    id: '',
    topic: '',
    types: [],
    campaigns: [],
    units: [],
    departments: [],
    leadExperts: [],
  };
  const safeFallback = fallback ?? {
    id: '',
    topic: '',
    types: [],
    campaigns: [],
    units: [],
    departments: [],
    leadExperts: [],
  };

  return {
    id: safePrimary.id || safeFallback.id,
    topic: safePrimary.topic || safeFallback.topic,
    types: safePrimary.types.length > 0 ? safePrimary.types : safeFallback.types,
    campaigns: safePrimary.campaigns.length > 0 ? safePrimary.campaigns : safeFallback.campaigns,
    units: safePrimary.units.length > 0 ? safePrimary.units : safeFallback.units,
    departments: safePrimary.departments.length > 0 ? safePrimary.departments : safeFallback.departments,
    leadExperts: safePrimary.leadExperts.length > 0 ? safePrimary.leadExperts : safeFallback.leadExperts,
  };
}
